import type { z } from 'zod';

export class QueryHttpError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'QueryHttpError';
  }
}

export interface FetchJsonOptions extends RequestInit {
  /** Bearer credential; omitted when null. */
  credential?: string | null;
  /** Serialized as the JSON body with the matching content type. */
  json?: unknown;
  /** Message attached to the thrown `QueryHttpError`; UI copy is mapped by callers. */
  failure?: string;
}

/** `fetch` → status check → schema parse. Throws `QueryHttpError(status)` for non-2xx responses. */
export async function fetchJson<T extends z.ZodType>(
  schema: T,
  input: string,
  { credential, json, failure, headers, ...init }: FetchJsonOptions = {},
): Promise<z.output<T>> {
  const requestHeaders: Record<string, string> = {
    ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...(credential ? { Authorization: `Bearer ${credential}` } : {}),
    ...(headers as Record<string, string> | undefined),
  };
  const request: RequestInit = {
    ...init,
    ...(Object.keys(requestHeaders).length ? { headers: requestHeaders } : {}),
    ...(json !== undefined ? { body: JSON.stringify(json) } : {}),
  };
  const response = Object.keys(request).length ? await fetch(input, request) : await fetch(input);
  if (!response.ok)
    throw new QueryHttpError(failure ?? `Request failed (${response.status})`, response.status);
  return schema.parse(await response.json());
}
